 import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Tabs, Input, Pagination, Row, Col, Card } from 'antd';
import { TagOutlined, PercentageOutlined } from '@ant-design/icons';

const { TabPane } = Tabs;

function Product() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentImage, setCurrentImage] = useState('');
  const [currentProductId, setCurrentProductId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [totalProducts, setTotalProducts] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(48);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, []);

  const fetchCategories = async () => {
    try {
      const q = query(collection(db, 'products'));
      const querySnapshot = await getDocs(q);
      const categoriesSet = new Set();
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const category = data?.category;
        if (category) categoriesSet.add(category);
      });
      setCategories([...categoriesSet]);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async (category = null, search = '', page = 1) => {
    setLoading(true);
    try {
      let q;
      if (category) {
        q = query(collection(db, 'products'), where('category', '==', category));
      } else {
        q = query(collection(db, 'products'));
      }

      const querySnapshot = await getDocs(q);
      const totalProductsCount = querySnapshot.size;
      setTotalProducts(totalProductsCount);

      // Safely filter products
      const productsData = querySnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((product) => {
          const title = product?.title || '';
          const productCategory = product?.category || '';
          const searchTerm = search || '';
          
          return (
            title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            productCategory.toLowerCase().includes(searchTerm.toLowerCase())
          );
        });

      const paginatedProducts = productsData.slice(
        (page - 1) * productsPerPage,
        page * productsPerPage
      );

      setProducts(paginatedProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setCurrentPage(1);
    fetchProducts(null, value, 1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchProducts(null, searchQuery, page);
  };

  const calculateDiscountedPrice = (price, discount) => {
    return price - (price * discount) / 100;
  };

  const handleImageClick = (productId, imageUrl) => {
    setCurrentImage(imageUrl);
    setCurrentProductId(productId);
    setIsModalVisible(true);
  };

  const handleProductClick = (productId) => {
    navigate(`/product-details/${productId}`);
  };

  const handleOk = () => {
    navigate(`/product-details/${currentProductId}`);
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  return (
    <div style={{
      padding: '20px',
      maxWidth: '1400px',
      margin: '0 auto',
      backgroundColor: '#ffffff',
      minHeight: '100vh',
    }}>
      {/* Titles */}
      <h2 style={{
        textAlign: 'center',
        fontSize: '24px',
        fontWeight: 'bold',
        marginBottom: '10px',
        color: 'orange',
      }}>
        Exclusive Products
      </h2>
      
      <h2 style={{
        textAlign: 'center',
        fontSize: '20px',
        fontWeight: 'bold',
        marginBottom: '20px',
        color: '#333',
      }}>
        Dollar Furniture's
      </h2>

      {/* Promotional Banner (below titles, normal height - auto) */}
      <div style={{
        marginBottom: '30px',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
      }}>
        <img 
          src="https://firebasestorage.googleapis.com/v0/b/dollar-eda6d.firebasestorage.app/o/profile%2FChatGPT%20Image%20Jun%2014%2C%202026%2C%2012_50_51%20PM.png?alt=media&token=3820bf8b-1872-431e-91be-3add00710120"
          alt="Special Promotion Banner"
          style={{
            width: '100%',
            height: 'auto',
            display: 'block',
          }}
        />
      </div>

      <Input
        placeholder="Search products by title or category"
        value={searchQuery}
        onChange={handleSearchChange}
        style={{
          marginBottom: '20px',
          width: '100%',
          maxWidth: '500px',
          marginLeft: 'auto',
          marginRight: 'auto',
          display: 'block',
        }}
      />

      <div style={{ marginBottom: '20px' }}>
        <Tabs
          defaultActiveKey="all"
          onChange={(key) => {
            setCurrentPage(1);
            fetchProducts(key === 'all' ? null : key, searchQuery, 1);
          }}
          centered
        >
          <TabPane tab="All Products" key="all" />
          {categories.map((category) => (
            <TabPane 
              tab={category} 
              key={category} 
              style={{ textTransform: 'capitalize' }}
            />
          ))}
        </Tabs>
      </div>

      <Row gutter={[16, 16]} justify="center">
        {products.map((product) => {
          const newPrice = calculateDiscountedPrice(product.price, product.discount);
          return (
            <Col xs={12} sm={8} md={6} lg={6} key={product.id}>
              <Card
                hoverable
                style={{
                  width: '100%',
                  borderRadius: '10px',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  transition: 'transform 0.3s ease',
                }}
                bodyStyle={{ padding: '12px' }}
                onClick={() => handleProductClick(product.id)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{
                  position: 'relative',
                  width: '100%',
                  height: '150px',
                  overflow: 'hidden',
                  borderRadius: '8px',
                  marginBottom: '10px',
                }}>
                  <img
                    src={product.image}
                    alt={product.title || 'Product Image'}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleImageClick(product.id, product.image);
                    }}
                  />
                </div>
                
                <div style={{ textAlign: 'center' }}>
                  <h3 style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                    color: '#333',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '40px',
                    overflow: 'hidden',
                  }}>
                    <TagOutlined style={{ color: 'orangered', marginRight: '8px', fontSize: '16px' }} />
                    {product.title || 'No Title'}
                  </h3>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginBottom: '8px',
                    gap: '8px',
                  }}>
                    <span style={{
                      textDecoration: 'line-through',
                      color: '#999',
                      fontSize: '12px',
                    }}>
                      Ksh. {product.price || 0}
                    </span>
                    <span style={{
                      fontWeight: 'bold',
                      color: '#ff5722',
                      fontSize: '14px',
                    }}>
                      Ksh. {newPrice.toFixed(2)}
                    </span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'green',
                    fontSize: '12px',
                  }}>
                    <PercentageOutlined style={{ color: 'orangered', marginRight: '4px' }} />
                    {product.discount || 0}% OFF
                  </div>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>

      {products.length === 0 && !loading && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#999',
        }}>
          No products found.
        </div>
      )}

      {loading && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
        }}>
          <div className="ant-spin ant-spin-lg ant-spin-spinning">
            <span className="ant-spin-dot ant-spin-dot-spin">
              <i className="ant-spin-dot-item"></i>
              <i className="ant-spin-dot-item"></i>
              <i className="ant-spin-dot-item"></i>
              <i className="ant-spin-dot-item"></i>
            </span>
          </div>
        </div>
      )}

      <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'center' }}>
        <Pagination
          current={currentPage}
          total={totalProducts}
          pageSize={productsPerPage}
          onChange={handlePageChange}
          showSizeChanger={false}
        />
      </div>

      <Modal
        title="Product Image"
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleOk}>
            View Details
          </Button>,
        ]}
      >
        <img 
          src={currentImage} 
          alt="Product" 
          style={{ 
            width: '100%', 
            height: 'auto',
            borderRadius: '8px',
          }} 
        />
      </Modal>
    </div>
  );
}

export default Product;